import React, { Component } from 'react';
import { connect } from 'react-redux';
import SideBySideColumn from './SideBySideColumn/SideBySideColumn';
import classes from './SideBySideView.module.css';

const numberOfViewableColumns = (columnsContainer, columnWidth, maxNumberOfColumns, columnsList) => {
  // const pageWidth = columnsContainer && columnsContainer.style.maxWidth ? columnsContainer.style.maxWidth.replace('px', '') :  0;
  const pageWidth = columnsContainer ? columnsContainer.clientWidth : 0;
  let viewableColumnCount = columnWidth === 0 ? 0 : parseInt(pageWidth / columnWidth, 10);
  if (columnsList.length < viewableColumnCount) viewableColumnCount = columnsList.length;
  if (viewableColumnCount > maxNumberOfColumns) viewableColumnCount = maxNumberOfColumns;

  return viewableColumnCount;
};

const columnsToLoad = (
  columnsContainer,
  columnWidth,
  maxNumberOfColumns,
  leftColumnIndex,
  columnsList,
  columnsMapProperty,
  fetchedColumns,
  newColumnsOnly,
) => {
  const endIndex = leftColumnIndex + numberOfViewableColumns(columnsContainer, columnWidth, maxNumberOfColumns, columnsList);
  let columnsToFetch = columnsList.slice(leftColumnIndex, endIndex).map((column) => column[columnsMapProperty]);
  if (newColumnsOnly) columnsToFetch = columnsToFetch.filter((columnName) => !Object.keys(fetchedColumns).includes(columnName));
  return columnsToFetch;
};

const removedColumns = (previousFilters, currentFilters, columnType) => {
  const prevColumnNames = previousFilters[columnType].map((filter) => filter.name);
  const currentColumnNames = currentFilters[columnType].map((filter) => filter.name);
  const removedColumnNames = prevColumnNames.filter((columnName) => !currentColumnNames.includes(columnName));
  return removedColumnNames;
};

class SideBySideView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      viewableColumnCount: 0,
      leftColumnIndex: 0,
      columnWidth: 324.5,
      maxColumns: 9,
    };
    this.sideBySideContainer = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleBrowserWidthResize);

    if (this.props.columns.length > 0) this.handleInitialColumnSetup();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleBrowserWidthResize);
    // clear loaded columns when component is unmounted
    this.handleClearSideBySideColumns(this.props.type, this.props.columns);
  }

  componentDidUpdate(prevProps) {
    const columnType = this.props.type;

    // If the view type has changed (ie: user has navigated from '/category' to '/people') or the columns list has been loaded,
    // Fetch and persist initial columns
    if ((this.props.columns.length > 0 && prevProps.columns.length === 0) || this.props.type !== prevProps.type) {
      this.handleInitialColumnSetup();
      // If user has navigated to a different type  (eg: people -> categories), clear prevProps.type column contents and reset left index to 0
      if (this.props.type !== prevProps.type) {
        this.handleClearSideBySideColumns(prevProps.type, prevProps.columns);
        this.setState({ leftColumnIndex: 0 });
      }
    }

    // Handling situation when log filters are modified
    if (this.props.filters !== prevProps.filters) {
      // Determine whether column filter has been cleared, thus rendering all columns
      // const columnFilterCleared = this.props.filters[columnType].length === 0 && prevProps.filters[columnType].length > 0;

      // If any other filters were changed, then we need to clear out all columns and start from scratch
      const nonColumnsFilterChanged =
        this.props.filters[columnType] === prevProps.filters[columnType] ||
        (this.props.filters[columnType].length === 0 && prevProps.filters[columnType].length === 0);

      // Each filter is updated individually, if the filter that has changed is the columnType, then we need to discard any
      // removed columns and fetch all new columns
      const columnsToRemove = nonColumnsFilterChanged
        ? this.props.columns.map((column) => column.name)
        : removedColumns(prevProps.filters, this.props.filters, columnType);
      const fetchNewColumnsOnly = !nonColumnsFilterChanged;
      this.props.handleRemoveMultiViewColumns(this.props.type, columnsToRemove);
      this.fetchColumns(fetchNewColumnsOnly);
      // In the case that all filters have been cleared or the column related filter has changed, envoke this.setColumnCount
      if (!nonColumnsFilterChanged) this.setColumnCount(prevProps.columns, this.props.columns);
    }
  }

  handleClearSideBySideColumns = (type, columns) => {
    const loadedColumns = columns.map((column) => column.name);
    this.props.handleRemoveMultiViewColumns(type, loadedColumns);
  };

  handleInitialColumnSetup = () => {
    // Fetch columns
    this.fetchColumns(false, true);
    // persist the number of columns that are currently in view
    this.setColumnCount();
  };

  // Determine number of columns that should be in view and update this.state.viewableColumnCount accordingly
  setColumnCount = (prevColumns, currentColumns) => {
    const viewableColumnCount = numberOfViewableColumns(
      this.sideBySideContainer.current,
      this.state.columnWidth,
      this.state.maxColumns,
      this.props.columns,
    );

    const leftColumnIndex = this.state.leftColumnIndex;
    if (prevColumns && currentColumns) {
      // If the number of columns before/after the leftColumnIndex has changed, update leftColumnIndex accordingly
      // const prevLeftColumnName = prevColumns[this.state.leftColumnIndex] ? prevColumns[this.state.leftColumnIndex].name : '';
      // If previous left column no longer exists (is filtered out), then return left column to 0
      // const leftColumnIndex =
      //   currentColumns.findIndex((column) => column.name === prevLeftColumnName) > -1
      //     ? currentColumns.findIndex((column) => column.name === prevLeftColumnName)
      //     : 0;
    }
    this.setState({ viewableColumnCount, leftColumnIndex });
  };

  // In cases when there are less selected columns than the page can accomidate, to center the side by side view columns, we set the left css
  // attribute of the 'side-by-side-innermost-container' accordingly, //In cases when this.props.columns.length is sufficient to fill the
  // SideBySideViewContainer, left = 0px
  setInnerContainerLeftStyle = () => {
    const innerMostContainer = document.getElementById('side-by-side-innermost-container');
    if (innerMostContainer) {
      const innerContainerLeft =
        this.sideBySideContainer.current.clientWidth > innerMostContainer.clientWidth
          ? (this.sideBySideContainer.current.clientWidth - innerMostContainer.clientWidth) / 2
          : 0;
      innerMostContainer.style.left = `${innerContainerLeft}px`;
    }
  };

  // fetches all columns that are not already loaded
  fetchColumns = (newColumnsOnly, initialColumns) => {
    const viewType = this.props.type === 'categories' ? 'multiViewCategories' : 'multiViewPeople';
    // Determine how many and which columns to fetch
    const columnsMapProperty = this.props.type === 'categories' ? 'name' : 'UserId';
    const leftColumnIndex = initialColumns ? 0 : this.state.leftColumnIndex;
    const columnsToFetch = columnsToLoad(
      this.sideBySideContainer.current,
      this.state.columnWidth,
      this.state.maxColumns,
      leftColumnIndex,
      this.props.columns,
      columnsMapProperty,
      this.props[viewType],
      newColumnsOnly,
    );
    // fetch columns posts for columns that should be in view
    if (columnsToFetch.length > 0) this.props.handleFetchMultiViewColumns(this.props.type, columnsToFetch, 1);
  };

  fetchNewColumn = (columnName) => {
    const viewType = this.props.type === 'categories' ? 'multiViewCategories' : 'multiViewPeople';
    if (!Object.keys(this.props[viewType]).includes(columnName)) {
      this.props.handleFetchMultiViewColumns(this.props.type, [columnName], 1);
    }
  };

  handleBrowserWidthResize = () => {
    // Determine how many columns the view now accommidates more columns
    const updatedNumberOfViewableColumns = numberOfViewableColumns(
      this.sideBySideContainer.current,
      this.state.columnWidth,
      this.state.maxColumns,
      this.props.columns,
    );
    // If the browser width has been expanded enough for the the updatedNumberOfViewableColumns to be greater than the previous number of
    // viewable columns (viewableColumnCount)
    // Fetch posts for any columns who have been added to the screen, but, for which, posts have not yet been loaded
    if (updatedNumberOfViewableColumns > this.state.viewableColumnCount) this.fetchColumns(true);

    // in the case that the number of viewable columns is increasing, determine whether we need to reduce the leftColumnIndex to accomidate for
    // showing more columns
    const leftColumnIndex =
      this.state.leftColumnIndex + updatedNumberOfViewableColumns >= this.props.columns.length
        ? this.props.columns.length > updatedNumberOfViewableColumns
          ? this.props.columns.length - updatedNumberOfViewableColumns
          : 0
        : this.state.leftColumnIndex;
    // If anything has changed, update number of columns that should now be viewable && left column index
    if (updatedNumberOfViewableColumns !== this.state.viewableColumnCount) {
      this.setState({ viewableColumnCount: updatedNumberOfViewableColumns, leftColumnIndex });
    }
    // reset left position of innerMostContainer relateive to this.sideBySideContainer as browser expands/contracts
    this.setInnerContainerLeftStyle();
  };

  handleArrowClick = (direction) => {
    const updatedLeftColumnIndex =
      direction === 'previous'
        ? this.state.leftColumnIndex > 0
          ? this.state.leftColumnIndex - 1
          : 0
        : this.props.columns.length > this.state.leftColumnIndex + this.state.viewableColumnCount
        ? this.state.leftColumnIndex + 1
        : this.state.leftColumnIndex;
    // If column index has changed, envoke fetchNewColumn() to determine whether the newly rendered column has been fetch yet. And, if it has
    // not yet been fetched, then fetch
    const columnsMapProperty = this.props.type === 'categories' ? 'name' : 'UserId';
    if (updatedLeftColumnIndex !== this.state.leftColumnIndex) {
      const newlyRenderedColumnIndex =
        direction === 'previous' ? updatedLeftColumnIndex : updatedLeftColumnIndex + this.state.viewableColumnCount - 1;
      const newlyRenderedColumnName = this.props.columns[newlyRenderedColumnIndex][columnsMapProperty];
      this.fetchNewColumn(newlyRenderedColumnName);
      this.setState({ leftColumnIndex: updatedLeftColumnIndex });
    }
  };

  render = () => {
    let columns = [];

    if (this.props.columns.length > 0) {
      // const endIndex = this.state.leftColumnIndex + this.state.viewableColumnCount;
      const multiViewColumnData = this.props.type === 'categories' ? this.props.multiViewCategories : this.props.multiViewPeople;
      columns = this.props.columns.map((column, i) => {
        const columnsMapProperty = this.props.type === 'categories' ? 'name' : 'UserId';
        const columnName = this.props.type === 'categories' ? column.name : column.FirstName;
        const columnKey = this.props.type === 'categories' ? column.name : column.UserId;
        const columnColor = this.props.type === 'categories' ? null : column.Color;
        const showPreviousArrow = i === this.state.leftColumnIndex && this.state.leftColumnIndex > 0;
        const showNextArrow =
          this.props.columns.findIndex((c) => c[columnsMapProperty] === columnKey) ===
            this.state.leftColumnIndex + this.state.viewableColumnCount - 1 &&
          this.props.columns.findIndex((c) => c[columnsMapProperty] === columnKey) < this.props.columns.length - 1;

        return (
          <SideBySideColumn
            key={`column-${columnName}-${i}`}
            type={this.props.type}
            columnNumber={i}
            subType={columnName}
            columnColor={columnColor}
            columnImgSrc={this.props.type === 'memberIds' ? column.ThumbURL : null}
            title={columnName}
            columnKey={this.props.type === 'categories' ? columnName : column.UserId}
            category={columnName}
            multiViewColumnData={multiViewColumnData}
            filters={this.props.filters}
            selectedSortColumn={this.props.selectedSortColumn}
            postViewSize={this.props.postViewSize}
            showPreviousArrow={showPreviousArrow}
            showNextArrow={showNextArrow}
            handlePostSelect={this.props.handlePostSelect}
            handleArrowClick={this.handleArrowClick}
            handleFetchMultiViewColumns={this.props.handleFetchMultiViewColumns}
          />
        );
      });
    }
    const translateX = this.state.leftColumnIndex * this.state.columnWidth;
    this.setInnerContainerLeftStyle();
    return (
      <div ref={this.sideBySideContainer} className={classes.SideBySideViewContainer}>
        <div className={classes.SideBySideViewInnerContainer1}>
          <div
            id="side-by-side-innermost-container"
            style={{ transform: `translateX(-${translateX}px)` }}
            className={classes.SideBySideViewInnerContainer2}
          >
            {columns}
          </div>
        </div>
      </div>
    );
  };
}

const mapStateToProps = (state) => {
  return {
    multiViewCategories: state.logs.multiViewCategories,
    multiViewPeople: state.logs.multiViewPeople,
  };
};

export default connect(mapStateToProps)(SideBySideView);
